import { Text } from '@dixa/lib-shared-habla';
import type { SearchFilterCondition } from 'lib-search-types';
import { FunctionComponent, useMemo } from 'react';

import { isDateFilterField } from 'app/views/layout/views/search/utils/date/isDateFilterField';
import { ConditionFieldCustomAttribute } from './components/customAttribute/conditionFieldCustomAttribute';
import { ConditionFieldDate } from './components/date/conditionFieldDate';
import { ConditionFieldDuration } from './components/duration/conditionFieldDuration';
import {
  ConditionFieldAgents,
  ConditionFieldChannel,
  ConditionFieldCompany,
  ConditionFieldContacts,
  ConditionFieldDirection,
  ConditionFieldId,
  ConditionFieldQueue,
  ConditionFieldStatus,
  ConditionFieldTags,
  ConditionFieldText,
  ConditionFieldUserType,
} from './components/select/conditionFieldSelect';

/** Represents the props passed to a filter condition field. */
export type ConditionFieldProps<
  TCondition extends SearchFilterCondition<'conversations'>,
> = {
  /** The current filter condition. */
  condition: TCondition;
  /** The placeholder to show in the filter component input. */
  placeholder?: string;
  /** Whether the filter component input is disabled. */
  disabled?: boolean;
  /** The callback called when the value of the filter component input changes. */
  onChange: (values: TCondition['values']) => void;
  /** The callback called when an error occurs in a filter component. */
  onError?: (error: string) => void;
};

export const ConditionField = <
  TCondition extends SearchFilterCondition<'conversations'>,
>(
  props: ConditionFieldProps<TCondition>,
): JSX.Element => {
  const Component = useMemo(() => {
    if (isDateFilterField(props.condition.field)) {
      return ConditionFieldDate;
    }

    switch (props.condition.field) {
      case 'numeric_id':
        return ConditionFieldId;
      case 'status':
        return ConditionFieldStatus;
      case 'initial_direction':
        return ConditionFieldDirection;
      case 'initial_channel':
        return ConditionFieldChannel;
      case 'queue_id':
        return ConditionFieldQueue;
      case 'agent_id':
      case 'message_author_id':
      case 'note_author_id':
        return ConditionFieldAgents;
      case 'requested_by':
        return ConditionFieldContacts;
      case 'tags':
        return ConditionFieldTags;
      case 'company_id':
        return ConditionFieldCompany;
      case 'user_type':
        return ConditionFieldUserType;
      case 'duration':
        return ConditionFieldDuration;
      case 'note_message':
        return ConditionFieldText;

      case 'conversation_custom_attribute':
      case 'contact_custom_attribute':
        return ConditionFieldCustomAttribute;

      default:
        return Fallback;
    }
  }, [props.condition.field]) as FunctionComponent<
    ConditionFieldProps<TCondition>
  >;

  return <Component {...props} />;
};

const Fallback: FunctionComponent = () => (
  <Text.Paragraph variant="4" subdued>
    Not implemented
  </Text.Paragraph>
);
